/**
 * 双向链表节点
 */
export class DoublyLinkedNode<T> {
  value: T
  next: DoublyLinkedNode<T> | null = null
  prev: DoublyLinkedNode<T> | null = null

  constructor(value: T) {
    this.value = value
  }
}

/**
 * 双向链表实现
 */
export class DoublyLinkedList<T> {
  private head: DoublyLinkedNode<T> | null = null
  private tail: DoublyLinkedNode<T> | null = null
  private nodeMap = new Map<T, DoublyLinkedNode<T>>()

  /**
   * 添加节点到链表尾部
   * @param value 节点值
   */
  add(value: T): void {
    let node = this.nodeMap.get(value)
    if (node) {
      this.remove(value)
    }
    node = new DoublyLinkedNode(value)

    if (!this.head) {
      this.head = node
      this.tail = node
    } else {
      node.prev = this.tail
      this.tail!.next = node
      this.tail = node
    }
    this.nodeMap.set(value, node)
  }

  /**
   * 从链表中移除指定值的节点
   * @param value 要移除的节点值
   */
  remove(value: T): void {
    const node = this.nodeMap.get(value)
    if (!node) return

    if (node.prev) {
      node.prev.next = node.next
    } else {
      this.head = node.next
    }

    if (node.next) {
      node.next.prev = node.prev
    } else {
      this.tail = node.prev
    }

    this.nodeMap.delete(value)
  }

  /**
   * 获取链表头部节点的值
   * @returns 头部节点值，如果链表为空则返回null
   */
  getFirst(): T | null {
    return this.head?.value ?? null
  }

  /**
   * 获取链表尾部节点的值
   * @returns 尾部节点值，如果链表为空则返回null
   */
  getLast(): T | null {
    return this.tail?.value ?? null
  }

  /**
   * 获取链表中的节点数量
   */
  size(): number {
    return this.nodeMap.size
  }

  /**
   * 检查链表是否包含指定值的节点
   * @param value 要检查的值
   */
  has(value: T): boolean {
    return this.nodeMap.has(value)
  }

  /**
   * 清空链表
   */
  clear(): void {
    this.head = null
    this.tail = null
    this.nodeMap.clear()
  }
}
